import { getApiKeysApi } from '@/api-calls/api-keys/get'
import { createApiKeyApi } from '@/api-calls/api-keys/mutate'
import { CodeBlock, CopyButton } from '@/components/code-block'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import { getApiUrl } from '@/utils/api-utils'
import { Badge, Button, Skeleton } from '@blogging/ui'
import { cn } from '@blogging/utils'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import {
  Bot,
  ChevronDown,
  FilePenLine,
  History,
  KeyRound,
  Loader2,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

const MCP_URL = getApiUrl('/api/mcp')
const KEY_PLACEHOLDER = 'YOUR_API_KEY'

interface ApiKey {
  id: string
  name: string
  access: 'read' | 'write'
}

export function SiteSettingsMcpPage() {
  const { siteId } = useParams()
  const queryClient = useQueryClient()
  const [newKey, setNewKey] = useState<string | null>(null)

  const { data: apiKeys, isLoading } = useFetch(getApiKeysApi, {
    resourceName: 'apiKeys',
    initialParams: { siteId: siteId as string },
  })
  const agentKeys = ((apiKeys as ApiKey[] | undefined) ?? []).filter(
    (key) => key.access === 'write',
  )

  const { mutate: createKey, isPending: isCreating } = useMutation(
    createApiKeyApi,
    {
      resourceName: 'apiKey',
      onSuccess: (data: any) => {
        setNewKey(data.apiKey.secret)
        queryClient.invalidateQueries({
          queryKey: [getApiKeysApi.apiName, { siteId }],
        })
        toast.success('Agent key created')
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to create key')
      },
    },
  )

  const handleCreateKey = () =>
    createKey({
      siteId: siteId as string,
      name: `AI agent ${new Date().toLocaleDateString('en-US')}`,
      access: 'write',
    })

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">MCP Server</h1>
            <Badge variant="secondary">Beta</Badge>
          </div>
          <p className="text-muted-foreground">
            Let AI assistants like Claude or Cursor draft and edit your posts
            and pages. They work alongside your team: everything they write
            starts as a draft for you to review.
          </p>
        </div>

        <Step
          number={1}
          title="Create an agent key"
          description="The key lets your AI assistant into this site. It acts as you, so only share it with tools you trust."
        >
          {isLoading ? (
            <Skeleton className="h-16 w-full rounded-lg" />
          ) : newKey ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
              <p className="mb-2 text-sm text-amber-800 dark:text-amber-200">
                Copy this key now, you won&apos;t see it in full again. It has
                been filled into the setup instructions below.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded border bg-white px-3 py-2 font-mono text-sm dark:bg-gray-900">
                  {newKey}
                </code>
                <CopyButton text={newKey} />
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {agentKeys.length > 0 ? (
                  <>
                    You already have {agentKeys.length} agent key
                    {agentKeys.length > 1 ? 's' : ''} (
                    {agentKeys.map((key) => key.name).join(', ')}). Copy one
                    from{' '}
                    <Link
                      to={`/${siteId}/settings/api-keys`}
                      className="underline underline-offset-4"
                    >
                      API Keys
                    </Link>
                    , or create a new one.
                  </>
                ) : (
                  'No agent keys yet. Create one to get started.'
                )}
              </p>
              <Button
                onClick={handleCreateKey}
                disabled={isCreating}
                className="shrink-0"
              >
                {isCreating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="mr-2 h-4 w-4" />
                )}
                Create agent key
              </Button>
            </div>
          )}
        </Step>

        <Step
          number={2}
          title="Connect your AI assistant"
          description="Pick the tool you use and follow its steps."
        >
          <ClientSetup apiKey={newKey ?? KEY_PLACEHOLDER} />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Server URL:</span>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {MCP_URL}
            </code>
            <CopyButton text={MCP_URL} />
          </div>
        </Step>

        <Step
          number={3}
          title="Ask it to write"
          description="Talk to your assistant in plain language. A few things to try:"
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {EXAMPLE_PROMPTS.map((prompt) => (
              <div
                key={prompt}
                className="flex items-start justify-between gap-2 rounded-lg border bg-card p-3 text-sm"
              >
                <span>&ldquo;{prompt}&rdquo;</span>
                <CopyButton text={prompt} className="shrink-0" />
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Drafts show up in your Posts and Pages lists like any other draft.
            Open one in the editor to review it, then publish when you&apos;re
            happy.
          </p>
        </Step>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">Built-in safeguards</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {SAFEGUARDS.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex gap-3 rounded-lg border p-4">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">{title}</p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <ToolReference />
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}

function Step({
  number,
  title,
  description,
  children,
}: {
  number: number
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <section className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {number}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold leading-8">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {children}
      </div>
    </section>
  )
}

function ClientSetup({ apiKey }: { apiKey: string }) {
  const [active, setActive] = useState(CLIENTS[0].id)
  const client = CLIENTS.find((c) => c.id === active) ?? CLIENTS[0]

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap gap-1 border-b p-1">
        {CLIENTS.map((c) => (
          <button
            key={c.id}
            onClick={() => setActive(c.id)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm transition-colors',
              c.id === active
                ? 'bg-muted font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="space-y-3 p-4">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          {client.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <CodeBlock code={client.snippet(apiKey)} />
        {apiKey === KEY_PLACEHOLDER && (
          <p className="text-xs text-muted-foreground">
            Replace <code className="font-mono">{KEY_PLACEHOLDER}</code> with
            your agent key from step 1.
          </p>
        )}
      </div>
    </div>
  )
}

function ToolReference() {
  const [open, setOpen] = useState(false)

  return (
    <section className="rounded-lg border">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between p-4 text-left"
      >
        <div>
          <p className="font-medium">Tool reference</p>
          <p className="text-sm text-muted-foreground">
            The tools your assistant sees. Useful if you&apos;re building your
            own agent.
          </p>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="divide-y border-t">
          {TOOLS.map((tool) => (
            <div
              key={tool.name}
              className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <code className="w-56 shrink-0 font-mono text-xs">
                {tool.name}
              </code>
              <span className="flex-1 text-sm text-muted-foreground">
                {tool.description}
              </span>
              <Badge
                variant={tool.write ? 'default' : 'secondary'}
                className="w-fit whitespace-nowrap"
              >
                {tool.write ? 'Write' : 'Read'}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

interface ClientGuide {
  id: string
  label: string
  steps: string[]
  snippet: (apiKey: string) => string
}

const jsonConfig = (apiKey: string) =>
  JSON.stringify(
    {
      mcpServers: {
        blogging: {
          url: MCP_URL,
          headers: { Authorization: `Bearer ${apiKey}` },
        },
      },
    },
    null,
    2,
  )

const CLIENTS: ClientGuide[] = [
  {
    id: 'claude-code',
    label: 'Claude Code',
    steps: [
      'Run this command in your terminal.',
      'Start Claude Code and type /mcp to check that "blogging" is connected.',
    ],
    snippet: (apiKey) =>
      `claude mcp add --transport http blogging ${MCP_URL} \\\n  --header "Authorization: Bearer ${apiKey}"`,
  },
  {
    id: 'claude-desktop',
    label: 'Claude Desktop',
    steps: [
      'Open Claude Desktop → Settings → Developer → Edit Config.',
      'Add the "blogging" entry below to claude_desktop_config.json (requires Node.js).',
      'Restart Claude Desktop.',
    ],
    snippet: (apiKey) =>
      JSON.stringify(
        {
          mcpServers: {
            blogging: {
              command: 'npx',
              args: [
                'mcp-remote',
                MCP_URL,
                '--header',
                `Authorization: Bearer ${apiKey}`,
              ],
            },
          },
        },
        null,
        2,
      ),
  },
  {
    id: 'cursor',
    label: 'Cursor',
    steps: [
      'Open Cursor Settings → MCP → Add new MCP server, or edit ~/.cursor/mcp.json.',
      'Paste the configuration below and save.',
    ],
    snippet: jsonConfig,
  },
  {
    id: 'other',
    label: 'Other tools',
    steps: [
      'Use any client that supports remote MCP servers over Streamable HTTP.',
      'Point it at the server URL and send your key as a Bearer token.',
    ],
    snippet: jsonConfig,
  },
]

const EXAMPLE_PROMPTS = [
  'Draft a post about what we shipped this week, using these notes: …',
  'Fix typos and tighten the wording in my latest draft.',
  'Turn this outline into an About page.',
  'Suggest tags for my last five posts and apply them.',
]

const SAFEGUARDS = [
  {
    icon: FilePenLine,
    title: 'Drafts first',
    description:
      'New posts and pages are saved as drafts. Agents only publish when you explicitly ask them to.',
  },
  {
    icon: History,
    title: "Won't overwrite your edits",
    description:
      'If someone changed a post since the agent last read it, the agent has to re-read it before saving.',
  },
  {
    icon: UserCheck,
    title: 'Acts as you',
    description:
      'Changes are attributed to the key creator and limited to what your role allows on this site.',
  },
  {
    icon: ShieldCheck,
    title: 'Revoke anytime',
    description:
      'Delete the key in API Keys and the agent loses access immediately.',
  },
  {
    icon: Sparkles,
    title: 'Keeps rich blocks safe',
    description:
      "Agents write Markdown. They won't rewrite content that has embeds, galleries or math they can't represent.",
  },
  {
    icon: Bot,
    title: 'Read-only keys stay read-only',
    description:
      'Keys without write access can read content but never change it.',
  },
]

const TOOLS = [
  {
    name: 'get_site',
    description: 'Site details and your access level',
    write: false,
  },
  { name: 'list_tags', description: 'Tags with post counts', write: false },
  {
    name: 'list_posts / list_pages',
    description: 'Browse and filter content',
    write: false,
  },
  {
    name: 'get_post / get_page',
    description: 'Read content as Markdown',
    write: false,
  },
  {
    name: 'create_post / create_page',
    description: 'Create a draft from Markdown',
    write: true,
  },
  {
    name: 'update_post / update_page',
    description: 'Change title, content, tags, status…',
    write: true,
  },
  {
    name: 'edit_post / edit_page',
    description: 'Find-and-replace edits in the content',
    write: true,
  },
]
